import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  ArrowLeft, Settings, GitBranch, CheckCircle, XCircle, Loader2, Link2, Unlink, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../lib/api';

export default function SettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bbSettings, setBbSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bbUsername, setBbUsername] = useState('');
  const [bbPassword, setBbPassword] = useState('');
  const [bbWorkspace, setBbWorkspace] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const settings = await api.getBitbucketSettings();
      setBbSettings(settings);
      setBbUsername(settings.username || '');
      setBbWorkspace(settings.workspace || '');
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleConnect = async () => {
    if (!bbUsername.trim() || !bbPassword.trim()) {
      toast.error('Username e App Password richiesti');
      return;
    }
    setSaving(true);
    try {
      const result = await api.saveBitbucketSettings({
        username: bbUsername.trim(),
        app_password: bbPassword.trim(),
        workspace: bbWorkspace.trim()
      });
      setBbSettings(result);
      setBbPassword('');
      toast.success(`Connesso come ${result.display_name || bbUsername}!`);
    } catch (e) {
      toast.error(e.message);
    } finally { setSaving(false); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnettere Bitbucket?')) return;
    try {
      await api.disconnectBitbucket();
      setBbSettings({ connected: false });
      setBbUsername('');
      setBbPassword('');
      setBbWorkspace('');
      toast.success('Bitbucket disconnesso');
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0C]" data-testid="settings-page">
      <div className="h-14 flex items-center px-6 border-b border-white/[0.06] bg-[#101013]">
        <Link to="/chat" className="flex items-center gap-2 text-white/50 hover:text-white transition-colors mr-6">
          <ArrowLeft size={18} />
          <span className="text-sm">Indietro</span>
        </Link>
        <Settings size={18} className="text-white/60 mr-2" />
        <span className="text-white font-semibold text-lg">Impostazioni</span>
      </div>

      <div className="max-w-2xl mx-auto p-6 space-y-6">
        {/* User Info */}
        <Card className="bg-[#101013] border-white/[0.08]">
          <CardHeader>
            <CardTitle className="text-white text-sm font-medium">Profilo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center text-white font-bold text-lg">
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
              <div>
                <div className="text-white font-medium">{user?.name}</div>
                <div className="text-white/40 text-sm">{user?.email}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bitbucket Integration */}
        <Card className="bg-[#101013] border-white/[0.08]">
          <CardHeader>
            <CardTitle className="text-white text-sm font-medium flex items-center gap-2">
              <GitBranch size={16} className="text-blue-400" />
              Bitbucket
              {bbSettings?.connected && (
                <Badge className="bg-green-500/10 text-green-400 text-[10px]">
                  <CheckCircle size={10} className="mr-1" /> Connesso
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-6"><Loader2 className="animate-spin text-white/30" size={20} /></div>
            ) : bbSettings?.connected ? (
              <div className="space-y-4">
                <div className="flex items-center gap-4 bg-white/[0.03] rounded-lg p-4">
                  {bbSettings.avatar && (
                    <img src={bbSettings.avatar} alt="" className="h-10 w-10 rounded-full" />
                  )}
                  <div className="flex-1">
                    <div className="text-white text-sm font-medium">{bbSettings.display_name || bbSettings.username}</div>
                    <div className="text-white/40 text-xs">@{bbSettings.username} · {bbSettings.workspace}</div>
                  </div>
                  {bbSettings.verified ? (
                    <Badge className="bg-green-500/10 text-green-400 text-[10px]"><CheckCircle size={10} className="mr-1" /> Verificato</Badge>
                  ) : (
                    <Badge className="bg-red-500/10 text-red-400 text-[10px]"><XCircle size={10} className="mr-1" /> Errore</Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleDisconnect} className="border-red-500/20 text-red-400 hover:bg-red-500/10">
                    <Unlink size={13} className="mr-1.5" /> Disconnetti
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => window.open(`https://bitbucket.org/${bbSettings.workspace}`, '_blank')}
                    className="border-white/10 text-white/50 hover:bg-white/[0.04]">
                    <ExternalLink size={13} className="mr-1.5" /> Apri Bitbucket
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-white/40 text-sm">
                  Collega il tuo account Bitbucket per importare repo, pushare codice e creare Pull Request direttamente da IAIA.
                </p>
                <Separator className="bg-white/[0.06]" />
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-white/60 text-xs">Username Bitbucket</Label>
                    <Input value={bbUsername} onChange={(e) => setBbUsername(e.target.value)}
                      placeholder="il-tuo-username" className="bg-white/[0.04] border-white/[0.08] text-white h-9"
                      data-testid="bb-username-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-white/60 text-xs">
                      API Token
                      <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noreferrer"
                        className="ml-2 text-blue-400/60 hover:text-blue-400 text-[10px]">
                        Come crearlo? <ExternalLink size={9} className="inline" />
                      </a>
                    </Label>
                    <Input value={bbPassword} onChange={(e) => setBbPassword(e.target.value)}
                      type="password" placeholder="ATATT3xFfGF0..." className="bg-white/[0.04] border-white/[0.08] text-white h-9"
                      data-testid="bb-password-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-white/60 text-xs">Workspace (nome del team)</Label>
                    <Input value={bbWorkspace} onChange={(e) => setBbWorkspace(e.target.value)}
                      placeholder="es. toyou_limitless" className="bg-white/[0.04] border-white/[0.08] text-white h-9"
                      data-testid="bb-workspace-input" />
                  </div>
                </div>
                <Button onClick={handleConnect} disabled={saving || !bbUsername || !bbPassword}
                  className="bg-white text-black hover:bg-white/90 w-full" data-testid="bb-connect-button">
                  {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Link2 size={14} className="mr-2" />}
                  Connetti Bitbucket
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
